import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&quot;{quote}&quot;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function TamesideCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Tameside Pothole Claims | RED Best Practice Rating | Fixtyer"
        description="Tameside MBC earns RED for best practice despite GREEN spend. 80% of its network is U-roads on a four-year CVI survey cycle, U-road RED condition hit 15.4%, and the Highway Asset Management Plan is still awaiting formal approval. See what the DfT AMBER rating means for your claim."
        canonical="/councils/tameside"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Tameside', url: '/councils/tameside' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'What does Tameside\'s RED best practice rating mean for my pothole claim?',
            answer: 'The DfT has formally scored Tameside\'s highways asset management approach as failing national best-practice standards. The council\'s own transparency report states its Highway Asset Management Plan and supporting documents are "currently being updated" and will only be published "once formally approved", with the Skid Resistance Plan still "pending approval". That undermines a Section 58 defence based on a mature, fully documented maintenance system.',
          },
          {
            question: 'Tameside has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. GREEN spend reflects that Tameside\'s capital and revenue investment meets DfT thresholds, but the overall rating is AMBER and best practice is RED. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. In 2024/25 the council\'s own figures show 53% of maintenance spend was reactive, and it filled an estimated 4,472 potholes across its 707km network.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 565km — 80% of Tameside\'s 707km carriageway network. The council surveys U-roads via CVI on a four-year cycle, meaning only around a quarter of the residential network is condition-assessed in any given year. At the latest survey, 15.4% of U-roads were in RED condition (up from 13.0% in 2020), with green-condition U-roads falling from 83.5% to 70.1%.',
          },
          {
            question: 'Does the two-year SCANNER cycle on A and B/C roads help the council\'s Section 58 defence?',
            answer: 'A-roads (74km) and B/C roads (68km) together account for just 142km — 20% of the network. SCANNER covers them on a two-year cycle, so half the classified network has no fresh condition data in any given year. For the 80% of the borough that is U-roads, the gap is wider still: a four-year CVI cycle. Whether your road was surveyed recently is a factual question the council must answer with records, not assumptions.',
          },
          {
            question: 'Does filling 19,814 potholes in five years mean the roads are fixed?',
            answer: 'No. Tameside repaired an estimated 19,814 potholes over five years — an average of 3,962 per year, including 4,472 in 2024/25 alone (roughly 12 every day). The council\'s 2025/26 programme explicitly aims to "reduce the number of reactive repairs" while delivering "larger patching works" because they are "more cost effective, and sustainable, than repairing individual pot holes" — an admission that reactive pothole filling is not the same as fixing the network.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Tameside Metropolitan Borough Council"
        councilSlug="tameside"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/tameside"
        headline="Tameside Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Tameside</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Tameside: RED Best Practice on a Four-Year Survey Cycle
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Tameside Metropolitan Borough Council meets DfT spend thresholds and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the DfT rates its overall
            performance <strong className="text-amber-400">AMBER</strong> and its asset management approach{' '}
            <strong className="text-rose-400">RED</strong> — because the council&apos;s own transparency report
            reveals its Highway Asset Management Plan is still awaiting formal approval, U-road RED condition
            has risen to <strong className="text-rose-400">15.4%</strong> on a network where{' '}
            <strong className="text-rose-400">80% of carriageways are surveyed only once every four years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">RED</div>
            <div className="text-white font-medium mt-2">Best practice scorecard</div>
            <div className="text-brand-300 text-sm mt-1">
              While the HAMP and supporting documents are &quot;currently being updated&quot; and the Skid
              Resistance Plan remains &quot;pending approval&quot; — the council&apos;s own governance framework
              for Section 58 is not yet complete.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* RED Best Practice */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            The DfT&apos;s RED scorecard is not a headline — it reflects what Tameside&apos;s own report admits
            about its asset management framework
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The HAMP and supporting documents is currently being updated and will be added to the council's highway network webpage once formally approved."
              source="Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="This document will be uploaded onto the council's website once formal approval has been granted and will supersede the HAMP 2015 which can be found currently on the council's website: PolicyandStrategy.pdf"
              source="Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Incomplete Governance Framework</h3>
              <p className="text-rose-800 text-sm mb-3">
                The council lists linked documents that underpin its Highway Asset Management Plan — including
                a Highway Management Communication Plan, Performance Management Plan, Risk Management Plan,
                Winter Service Policy, and a Skid Resistance Plan that is still &quot;pending approval&quot;.
              </p>
              <p className="text-rose-800 text-sm">
                A Section 58 defence typically relies on showing a reasonable, documented system of inspection
                and maintenance. Tameside&apos;s own report confirms that system is still being finalised.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What The DfT Penalised</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Asset management plan not yet formally approved or published</li>
                <li>• Skid Resistance Plan still pending approval</li>
                <li>• HAMP 2015 still the live document on the council website</li>
                <li>• U-road condition data on a four-year CVI cycle across 565km</li>
                <li>• 53% of 2024/25 maintenance spend classified as reactive</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 And Best Practice</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Tameside must maintain its roads. Under Section 58,
              it can defend a claim by proving it took such care as was reasonable to secure that the part of
              the highway in question was not dangerous. A RED best-practice rating signals the DfT does not
              consider Tameside&apos;s published approach to meet national standards — ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Which version of the HAMP governed inspections when your defect formed?</li>
              <li>• Was the Skid Resistance Plan approved and in force at the time?</li>
              <li>• Can the council produce the Risk Management Plan it cites but has not yet published?</li>
              <li>• How does an asset management system still awaiting approval demonstrate reasonable care?</li>
            </ul>
            <p className="text-white font-medium">
              The council cannot point to a fully approved, published asset management framework — because
              its own report says it does not have one yet.
            </p>
          </div>
        </div>
      </section>

      {/* Network Size */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 565km Residential Majority
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Tameside&apos;s own transparency report — where most pothole claims actually happen
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey method</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey cycle</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">74</td>
                  <td className="px-6 py-4 text-center">10.5%</td>
                  <td className="px-6 py-4 text-center">SCANNER</td>
                  <td className="px-6 py-4 text-center">Two-year cycle</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">68</td>
                  <td className="px-6 py-4 text-center">9.6%</td>
                  <td className="px-6 py-4 text-center">SCANNER</td>
                  <td className="px-6 py-4 text-center">Two-year cycle</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-bold text-amber-900">U roads</td>
                  <td className="px-6 py-4 text-center font-bold font-mono text-amber-900">565</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-900">79.9%</td>
                  <td className="px-6 py-4 text-center">CVI</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">Four-year cycle</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageways</td>
                  <td className="px-6 py-4 text-center font-bold font-mono">707</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                  <td className="px-6 py-4 text-center">—</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="The condition of U roads is determined by a CVI survey which is completed every year on a four-year cycle to cover the full network."
            source="Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="mt-8 bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Four-Year Blind Spot</h3>
            <p className="text-brand-200 mb-4">
              On a four-year CVI cycle, roughly 141km of U-roads are condition-surveyed each year — leaving
              approximately 424km without a fresh network-level assessment in any given year. For a pothole
              claim on a residential street in Ashton, Hyde, Stalybridge or Dukinfield, the critical Section 58
              questions are:
            </p>
            <ul className="text-brand-300 space-y-2">
              <li>• When was your specific road last included in the CVI programme?</li>
              <li>• Did the council rely on complaint-driven inspections between surveys?</li>
              <li>• If 15.4% of U-roads are RED, what action was taken on yours before your incident?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Condition Data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of RCI survey data from Tameside&apos;s own transparency report — classified roads stable,
            U-roads deteriorating
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (74km — 10.5% of network): broadly stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.2%</td>
                    <td className="px-4 py-2 text-center">25.6%</td>
                    <td className="px-4 py-2 text-center">71.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.6%</td>
                    <td className="px-4 py-2 text-center">18.4%</td>
                    <td className="px-4 py-2 text-center">78.0%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">19.6%</td>
                    <td className="px-4 py-2 text-center">75.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.0%</td>
                    <td className="px-4 py-2 text-center">21.2%</td>
                    <td className="px-4 py-2 text-center">73.8%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.8%</td>
                    <td className="px-4 py-2 text-center font-bold">23.1%</td>
                    <td className="px-4 py-2 text-center font-bold">72.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED has edged up from 3.2% to 4.8% since 2020, and green has slipped from a 2021 peak of
              78.0% to 72.1%. Main roads are not the problem — they are one-tenth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (68km — 9.6% of network): stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">25.8%</td>
                    <td className="px-4 py-2 text-center">71.5%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.7%</td>
                    <td className="px-4 py-2 text-center">18.4%</td>
                    <td className="px-4 py-2 text-center">79.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">18.8%</td>
                    <td className="px-4 py-2 text-center">78.5%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">20.1%</td>
                    <td className="px-4 py-2 text-center">77.2%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">2.4%</td>
                    <td className="px-4 py-2 text-center font-bold">21.2%</td>
                    <td className="px-4 py-2 text-center font-bold">76.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads perform well on paper — but at 68km they are less than a tenth of the borough. The
              AMBER condition rating is driven by what is happening on U-roads.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (565km — 80% of network): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">13.0%</td>
                    <td className="px-4 py-2 text-center">3.5%</td>
                    <td className="px-4 py-2 text-center">83.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">12.0%</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                    <td className="px-4 py-2 text-center">84.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">12.9%</td>
                    <td className="px-4 py-2 text-center">6.3%</td>
                    <td className="px-4 py-2 text-center">80.8%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">12.7%</td>
                    <td className="px-4 py-2 text-center">13.1%</td>
                    <td className="px-4 py-2 text-center">74.2%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">14.5%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">70.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road RED condition has risen from 13.0% to 15.4% since 2020 — roughly 87km of residential
              streets now classified as requiring maintenance within 12 months. Green-condition U-roads have
              fallen from 83.5% to 70.1%, and amber has quadrupled from 3.5% to 14.5%.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, Reactive Reality</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.536m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.974m</div>
                <div className="text-brand-300 text-sm">Budgeted total spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">53%</div>
                <div className="text-brand-300 text-sm">Reactive share of spend in 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Tameside earns GREEN on spend — but in 2024/25 more than half of maintenance expenditure was
              reactive, and U-roads kept deteriorating. The 2025/26 budget targets 71% preventative capital
              spend, which is a plan, not proof the network is fixed.
            </p>
          </div>
        </div>
      </section>

      {/* Spending table */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years Of Maintenance Spend
          </h2>
          <p className="text-surface-600 mb-8">
            Capital allocation, actual spend, and preventative vs reactive split from the council&apos;s own report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 budget</td>
                  <td className="px-4 py-3 text-center">3,536,000</td>
                  <td className="px-4 py-3 text-center">3,536,000</td>
                  <td className="px-4 py-3 text-center">1,438,000</td>
                  <td className="px-4 py-3 text-center font-bold">4,974,000</td>
                  <td className="px-4 py-3 text-center text-emerald-600 font-bold">71%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">3,536,000</td>
                  <td className="px-4 py-3 text-center">1,771,526</td>
                  <td className="px-4 py-3 text-center">1,571,875</td>
                  <td className="px-4 py-3 text-center font-bold">3,343,401</td>
                  <td className="px-4 py-3 text-center">47%</td>
                  <td className="px-4 py-3 text-center text-rose-600 font-bold">53%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">4,051,493</td>
                  <td className="px-4 py-3 text-center">3,100,000</td>
                  <td className="px-4 py-3 text-center">2,673,000</td>
                  <td className="px-4 py-3 text-center font-bold">5,773,000</td>
                  <td className="px-4 py-3 text-center">46%</td>
                  <td className="px-4 py-3 text-center">54%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">3,536,000</td>
                  <td className="px-4 py-3 text-center">4,422,000</td>
                  <td className="px-4 py-3 text-center">3,621,000</td>
                  <td className="px-4 py-3 text-center font-bold">8,043,000</td>
                  <td className="px-4 py-3 text-center">45%</td>
                  <td className="px-4 py-3 text-center">55%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">3,915,000</td>
                  <td className="px-4 py-3 text-center">4,210,000</td>
                  <td className="px-4 py-3 text-center">3,353,000</td>
                  <td className="px-4 py-3 text-center font-bold">7,563,000</td>
                  <td className="px-4 py-3 text-center">44%</td>
                  <td className="px-4 py-3 text-center">56%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">2,315,724</td>
                  <td className="px-4 py-3 text-center">5,752,000</td>
                  <td className="px-4 py-3 text-center">2,426,000</td>
                  <td className="px-4 py-3 text-center font-bold">8,178,000</td>
                  <td className="px-4 py-3 text-center">30%</td>
                  <td className="px-4 py-3 text-center text-rose-600 font-bold">70%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Reactive Maintenance – works undertaken as a result of inspections or from complaints received that exceed the stated risk management intervention criteria. This includes, as an example, filing potholes, replacing failed lighting lanterns."
            source="Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Potholes */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            19,814 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes repaired</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,472</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">4,040</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,759</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,733</td>
                </tr>
                <tr className="border-b border-surface-100 bg-surface-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21*</td>
                  <td className="px-6 py-4 text-center text-surface-600 italic">Not listed separately</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19,814</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~12 Potholes a Day in 2024/25</h3>
              <p className="text-surface-600 text-sm">
                The council states it repaired 19,814 potholes over five years — an average of 3,962 each year.
                In 2024/25 alone, 4,472 estimated repairs works out at roughly 12 potholes filled every day
                across 707km. Defects that form between inspections are exactly where prior reports and
                photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Missing Year Row</h3>
              <p className="text-amber-800 text-sm">
                The published pothole table lists four individual years totalling 16,004 repairs, while the
                five-year headline is 19,814 — implying approximately 3,810 repairs in 2020/21 that the table
                does not break out. The report also labels one row &quot;2022 to 2024&quot; where the pattern
                suggests 2022/23. Treat year-on-year comparisons with care.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The 2025 / 2026 programme will have a targeted approach, based on the criteria above, in order to reduce the number of reactive repairs without being detrimental to the lifecycle of the network."
            source="Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="mt-6">
            <QuoteCard
              quote="Larger patching works will be delivered as this is more cost effective, and sustainable, than repairing individual pot holes."
              source="Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Forward plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Forward Plans And Methodology
          </h2>
          <p className="text-surface-600 mb-8">
            What the council says it will do — and how it measures condition today
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Inspection And Survey Methods</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• A and B/C roads: SCANNER laser survey, two-year cycle</li>
                <li>• U-roads: CVI survey, four-year cycle</li>
                <li>• RCI categories: Green (good), Amber (maintenance may be required soon), Red (maintenance within 12 months)</li>
                <li>• From 2026/27: PAS 2161 methodology with five categories instead of three</li>
                <li>• 30 KPIs monitored including % of network in red condition and public satisfaction</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Programme Priorities</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Priority preventative works on roads in early deterioration</li>
                <li>• Resurfacing prioritised by condition data, route importance, traffic, reactive history, density and complaints</li>
                <li>• Regular bridge and drainage inspections per national guidance</li>
                <li>• Street works coordination via Street Manager and Symology systems</li>
                <li>• Network Resilience Plan published on the council service charter page</li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="Tameside council recognises the importance of its highway infrastructure and how an effectively maintained and managed highway network is fundamental in supporting the local economy."
            source="Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER Council With GREEN Spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Tameside is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — meets DfT investment thresholds
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C road condition stable — RED at 2.4% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 2025/26 budget targets 71% preventative capital spend
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Member of LCRIG, APSE and Measure2Improve benchmarking groups
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual NHT stakeholder satisfaction survey commissioned
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured response on A and B/C roads with recent SCANNER data. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — HAMP and Skid Resistance Plan not yet formally approved
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED up from 13.0% to 15.4% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 80% of network on a four-year CVI survey cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 53% reactive spend in 2024/25 — majority reactive, not preventative
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 19,814 pothole repairs in five years — defects form faster than surveys catch them
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but RED best practice, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the four-year CVI cycle is your strongest structural argument</li>
              <li>• Whether the HAMP that was supposed to govern maintenance was even approved when your incident occurred</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Tameside&apos;s own transparency data — including the RED best-practice rating — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Tameside?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            RED best practice demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year U-road survey argument</li>
                <li>• No prior-report search</li>
                <li>• No HAMP approval-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED best-practice rating documented</li>
                <li>✅ Four-year U-road CVI cycle argued</li>
                <li>✅ 15.4% U-road RED condition cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Tameside</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=tameside"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Tameside Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Tameside&apos;s RED best practice rating mean for my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT has formally scored Tameside&apos;s highways asset management approach as failing national
                best-practice standards. The council&apos;s own transparency report states its Highway Asset Management
                Plan and supporting documents are &quot;currently being updated&quot; and will only be published
                &quot;once formally approved&quot;, with the Skid Resistance Plan still &quot;pending approval&quot;.
                That undermines a Section 58 defence based on a mature, fully documented maintenance system.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Tameside has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. GREEN spend reflects that Tameside&apos;s capital and revenue investment meets DfT thresholds,
                but the overall rating is AMBER and best practice is RED. Section 58 turns on whether the specific
                defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend.
                In 2024/25 the council&apos;s own figures show 53% of maintenance spend was reactive, and it filled
                an estimated 4,472 potholes across its 707km network.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 565km — 80% of Tameside&apos;s 707km carriageway network. The council surveys
                U-roads via CVI on a four-year cycle, meaning only around a quarter of the residential network is
                condition-assessed in any given year. At the latest survey, 15.4% of U-roads were in RED condition
                (up from 13.0% in 2020), with green-condition U-roads falling from 83.5% to 70.1%.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the two-year SCANNER cycle on A and B/C roads help the council&apos;s Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                A-roads (74km) and B/C roads (68km) together account for just 142km — 20% of the network. SCANNER
                covers them on a two-year cycle, so half the classified network has no fresh condition data in any
                given year. For the 80% of the borough that is U-roads, the gap is wider still: a four-year CVI
                cycle. Whether your road was surveyed recently is a factual question the council must answer with
                records, not assumptions.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does filling 19,814 potholes in five years mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Tameside repaired an estimated 19,814 potholes over five years — an average of 3,962 per year,
                including 4,472 in 2024/25 alone (roughly 12 every day). The council&apos;s 2025/26 programme
                explicitly aims to &quot;reduce the number of reactive repairs&quot; while delivering &quot;larger
                patching works&quot; because they are &quot;more cost effective, and sustainable, than repairing
                individual pot holes&quot; — an admission that reactive pothole filling is not the same as fixing
                the network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.tameside.gov.uk/servicecharter/roadfootpath"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Tameside Metropolitan Borough Council Local Highways Maintenance Transparency Report (October 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
